import axios from "axios";
import { FlynotesServerConfig, RealFlynotesServerConfig } from "./config";
import { Logging } from "./Logging";
import { R4PatientSyncData } from "./message-handlers/R4PatientSyncData";
import { R4ServerData } from "./ServerDataTypes";

export interface FlynotesServer {
  getLicenceKeyData(licenceKey: string): Promise<R4ServerData>;

  patientBulkUpload(csvData: string, licenceKey: string): Promise<void>;

  patientSync(syncData: R4PatientSyncData, licenceKey: string): Promise<void>;

  appointmentBulkUpload(csvData: string, licenceKey: string): Promise<void>;

  appointmentStatusBulkUpload(
    csvData: string,
    licenceKey: string
  ): Promise<void>;
}

export class RealFlynotesServer implements FlynotesServer {
  constructor(
    private readonly config: RealFlynotesServerConfig,
    private readonly logging: Logging
  ) {}

  async getLicenceKeyData(licenceKey: string): Promise<R4ServerData> {
    const response = await axios.get(
      `${this.config.url}/on-site-servers/${licenceKey}/data`,
      {
        validateStatus: function (status) {
          return status >= 200 && status < 499;
        },
      }
    );

    if (response.status !== 200)
      throw Error(
        `Licence key ${licenceKey} from ${this.config.url} was rejected. Status code was ${response.status}`
      );

    return response.data;
  }

  async patientBulkUpload(csvData: string, licenceKey: string): Promise<void> {
    const response = await axios.post(
      `${this.config.url}/on-site-servers/${licenceKey}/patient-upload?performUpsert=true`,
      csvData,
      {
        validateStatus: function (status) {
          return status >= 200 && status < 499;
        },
        headers: { "Content-Type": "text/csv" },
      }
    );

    if (response.status !== 200) {
      this.logging.log({
        message: "Patient bulk upload failed",
        response: response.data,
        statusCode: response.status,
      });

      throw Error(
        `Patient bulk upload to ${this.config.url} failed. Status code was ${response.status}`
      );
    }
    try {
      //await this.patientBulkUploadDevelopment(csvData);
    } catch (error) {}
  }

  async patientSync(
    syncData: R4PatientSyncData,
    licenceKey: string
  ): Promise<void> {
    const response = await axios.post(
      `${this.config.url}/on-site-servers/${licenceKey}/patient-sync`,
      syncData,
      {
        validateStatus: function (status) {
          return status >= 200 && status < 499;
        },
      }
    );

    if (response.status !== 200) {
      this.logging.log({
        message: "Patient sync failed",
        response: response.data,
        statusCode: response.status,
      });

      throw Error(
        `Patient sync to ${this.config.url} failed. Status code was ${response.status}`
      );
    }
    try {
      // await this.patientSyncDevelopment(syncData);
    } catch (error) {}
  }

  async appointmentBulkUpload(
    csvData: string,
    licenceKey: string
  ): Promise<void> {
    console.log(`appointment start bulk upload with ${csvData.length} items`);
    const response = await axios.post(
      `${this.config.url}/on-site-servers/${licenceKey}/appointment-upload?performUpsert=true`,
      csvData,
      {
        validateStatus: function (status) {
          return status >= 200 && status < 499;
        },
        headers: { "Content-Type": "text/csv" },
      }
    );
    console.log(
      `appointment start bulk upload with ${csvData.length} items with respond ${response.status} to development`
    );
    // await this.appointmentBulkUploadDevelopment(csvData);
    if (response.status !== 200) {
      this.logging.log({
        message: "Appointment bulk upload failed",
        response: response.data,
        statusCode: response.status,
      });
      throw Error(
        `Appointment bulk upload to ${this.config.url} failed. Status code was ${response.status}`
      );
    }
  }

  async appointmentStatusBulkUpload(
    csvData: string,
    licenceKey: string
  ): Promise<void> {
    const response = await axios.post(
      `${this.config.url}/on-site-servers/${licenceKey}/appointment-status-upload`,
      csvData,
      {
        validateStatus: function (status) {
          return status >= 200 && status < 499;
        },
        headers: { "Content-Type": "text/csv" },
      }
    );

    if (response.status !== 200) {
      this.logging.log({
        message: "Appointment status bulk upload failed",
        response: response.data,
        statusCode: response.status,
      });

      throw Error(
        `Appointment status bulk upload to ${this.config.url} failed. Status code was ${response.status}`
      );
    }
  }

 
}

export class StubFlynotesServer implements FlynotesServer {
  async getLicenceKeyData(licenceKey: string): Promise<R4ServerData> {
    return {
      sqsCredentials: {
        AccessKeyId: "access key",
        SecretAccessKey: "secret access key",
        SessionToken: "session token",
      },
      sqsQueueUrl: "https://queue.com",
      sqsRegion: "eu-west-2",
      libraryVersion: "0.0.3",
    };
  }

  patientBulkUpload(csvData: string, licenceKey: string): Promise<void> {
    throw Error("Not implemented");
  }

  patientSync(syncData: R4PatientSyncData, licenceKey: string): Promise<void> {
    throw Error("Not implemented");
  }

  appointmentBulkUpload(csvData: string, licenceKey: string): Promise<void> {
    throw Error("Not implemented");
  }

  appointmentStatusBulkUpload(
    csvData: string,
    licenceKey: string
  ): Promise<void> {
    throw Error("Not implemented");
  }
}

export function buildFlynotesServer(
  config: FlynotesServerConfig,
  logging: Logging
): FlynotesServer {
  switch (config.type) {
    case "real":
      return new RealFlynotesServer(config, logging);

    case "stub":
      return new StubFlynotesServer();
  }
}
